import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { BookingScheduleInfo, BookingSectionInfo } from '@/types/api';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import { getSyncStatusBadge } from '@/utils/syncStatus';

// Indexed by java.time.DayOfWeek convention used by the backend: 1=Monday .. 7=Sunday.
// Index 0 is unused so DAY_NAMES[dayOfWeek] maps directly.
const DAY_NAMES = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

interface SchedulesTabProps {
  restaurantId: number;
}

export function SchedulesTab({ restaurantId }: SchedulesTabProps) {
  const queryClient = useQueryClient();

  const canCreate = useHasPermission(PERM.BOOKING_CREATE);
  const canUpdate = useHasPermission(PERM.BOOKING_UPDATE);
  const canDelete = useHasPermission(PERM.BOOKING_DELETE);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<BookingScheduleInfo | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch schedules
  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ['booking-schedules', restaurantId],
    queryFn: () => apiClient.getBookingSchedules(restaurantId),
    enabled: !!restaurantId,
  });

  // Fetch sections for display
  const { data: sections = [] } = useQuery({
    queryKey: ['booking-sections', restaurantId],
    queryFn: () => apiClient.getBookingSections(restaurantId),
    enabled: !!restaurantId,
  });

  const sectionMap = new Map(sections.map((s) => [s.id, s.name]));

  // Delete mutation — uuid is the cross-table identifier.
  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => apiClient.deleteBookingSchedule(restaurantId, uuid),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['booking-schedules', restaurantId] });
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.message || 'Failed to delete schedule');
    },
  });

  const handleDelete = async (uuid: string | undefined) => {
    if (!uuid) return;
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      try {
        await deleteMutation.mutateAsync(uuid);
      } catch {
        // surfaced via deleteMutation's onError -> deleteError banner
      }
    }
  };

  const handleEdit = (schedule: BookingScheduleInfo) => {
    setEditingSchedule(schedule);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingSchedule(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingSchedule(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['booking-schedules', restaurantId] });
    setIsModalOpen(false);
    setEditingSchedule(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading schedules...</div>
      </div>
    );
  }

  return (
    <>
      {deleteError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center justify-between">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="text-red-700 hover:text-red-900 font-medium ml-4">
            Dismiss
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {schedules.length} {schedules.length === 1 ? 'schedule' : 'schedules'}
        </div>
        {canCreate && (
        <button onClick={handleCreate} className="btn btn-primary h-10">
          <Plus className="h-4 w-4 mr-2" />
          Add Schedule
        </button>
        )}
      </div>

      {/* Table */}
      {schedules.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500">No booking schedules configured yet.</p>
          <p className="text-sm text-gray-400 mt-1">
            Add a schedule to define booking availability for this restaurant.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Day of Week
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Open Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Close Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Enabled
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sections
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sync Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {schedules.map((schedule) => {
                  const badge = getSyncStatusBadge(schedule.syncStatus);
                  const sectionNames = schedule.sectionIds
                    .map((id) => sectionMap.get(id))
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <tr key={schedule.uuid ?? schedule.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {DAY_NAMES[schedule.dayOfWeek] ?? schedule.dayOfWeek}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {schedule.openTime}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {schedule.closeTime}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            schedule.enabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {schedule.enabled ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {sectionNames || '\u2014'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-1">
                          {canUpdate && (
                            <button
                              onClick={() => handleEdit(schedule)}
                              className="text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                              aria-label={`Edit schedule for ${DAY_NAMES[schedule.dayOfWeek]}`}
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(schedule.uuid as unknown as string)}
                              className="text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                              aria-label={`Delete schedule for ${DAY_NAMES[schedule.dayOfWeek]}`}
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <ScheduleModal
          restaurantId={restaurantId}
          schedule={editingSchedule}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
}

// Schedule Modal Component for Create/Edit
interface ScheduleModalProps {
  restaurantId: number;
  schedule: BookingScheduleInfo | null;
  onClose: () => void;
  onSuccess: () => void;
}

function ScheduleModal({ restaurantId, schedule, onClose, onSuccess }: ScheduleModalProps) {
  const [formData, setFormData] = useState({
    dayOfWeek: schedule?.dayOfWeek ?? 1,
    openTime: schedule?.openTime || '',
    closeTime: schedule?.closeTime || '',
    enabled: schedule?.enabled ?? true,
    sectionIds: schedule?.sectionIds || ([] as number[]),
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch sections for multi-select
  const { data: sections = [] } = useQuery({
    queryKey: ['booking-sections', restaurantId],
    queryFn: () => apiClient.getBookingSections(restaurantId),
    enabled: !!restaurantId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<BookingScheduleInfo>) =>
      apiClient.createBookingSchedule(restaurantId, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create schedule';
      setErrors({ submit: message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<BookingScheduleInfo>) =>
      apiClient.updateBookingSchedule(restaurantId, schedule!.uuid as unknown as string, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update schedule';
      setErrors({ submit: message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.openTime) {
      newErrors.openTime = 'Open time is required';
    }

    if (!formData.closeTime) {
      newErrors.closeTime = 'Close time is required';
    }

    if (formData.openTime && formData.closeTime && formData.closeTime <= formData.openTime) {
      newErrors.closeTime = 'Close time must be after open time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSectionToggle = (sectionId: number) => {
    setFormData((prev) => ({
      ...prev,
      sectionIds: prev.sectionIds.includes(sectionId)
        ? prev.sectionIds.filter((id) => id !== sectionId)
        : [...prev.sectionIds, sectionId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const submitData: Partial<BookingScheduleInfo> = {
      dayOfWeek: formData.dayOfWeek,
      openTime: formData.openTime,
      closeTime: formData.closeTime,
      enabled: formData.enabled,
      sectionIds: formData.sectionIds,
    };

    if (schedule) {
      await updateMutation.mutateAsync(submitData);
    } else {
      await createMutation.mutateAsync(submitData);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {schedule ? 'Edit Schedule' : 'Add New Schedule'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="schedule-day" className="block text-sm font-medium text-gray-700 mb-1">
              Day of Week *
            </label>
            <select
              id="schedule-day"
              value={formData.dayOfWeek}
              onChange={(e) => setFormData({ ...formData, dayOfWeek: parseInt(e.target.value) })}
              className="input w-full"
            >
              {DAY_NAMES.map((name, index) =>
                index === 0 ? null : (
                  <option key={index} value={index}>
                    {name}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label htmlFor="schedule-open-time" className="block text-sm font-medium text-gray-700 mb-1">
              Open Time *
            </label>
            <input
              id="schedule-open-time"
              type="time"
              value={formData.openTime}
              onChange={(e) => setFormData({ ...formData, openTime: e.target.value })}
              className={`input w-full ${errors.openTime ? 'border-red-500' : ''}`}
            />
            {errors.openTime && <p className="text-red-500 text-xs mt-1">{errors.openTime}</p>}
          </div>

          <div>
            <label htmlFor="schedule-close-time" className="block text-sm font-medium text-gray-700 mb-1">
              Close Time *
            </label>
            <input
              id="schedule-close-time"
              type="time"
              value={formData.closeTime}
              onChange={(e) => setFormData({ ...formData, closeTime: e.target.value })}
              className={`input w-full ${errors.closeTime ? 'border-red-500' : ''}`}
            />
            {errors.closeTime && <p className="text-red-500 text-xs mt-1">{errors.closeTime}</p>}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="schedule-enabled"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="schedule-enabled" className="ml-2 text-sm text-gray-700">
              Enabled
            </label>
          </div>

          {/* Section multi-select */}
          {sections.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sections
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                {sections.map((section: BookingSectionInfo) => (
                  <div key={section.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`schedule-section-${section.id}`}
                      checked={formData.sectionIds.includes(section.id)}
                      onChange={() => handleSectionToggle(section.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`schedule-section-${section.id}`}
                      className="ml-2 text-sm text-gray-700"
                    >
                      {section.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : schedule
                ? 'Update Schedule'
                : 'Create Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SchedulesTab;
